import { inTimeRange, localParts, parseHm } from './time';

export type ContentRef = { kind: 'playlist' | 'layout' | 'presentation' | 'media'; id: string };

export interface ScheduleRule {
  id: string;
  name?: string;
  content: ContentRef;
  /** 0=Sun … 6=Sat. Empty/undefined = every day. */
  days?: number[];
  /** "HH:MM". Both empty = all day. */
  startTime?: string | null;
  endTime?: string | null;
  /** "YYYY-MM-DD" inclusive bounds. */
  startDate?: string | null;
  endDate?: string | null;
  /** Higher wins. Ties: later rule in the list wins. */
  priority: number;
  enabled?: boolean;
}

export interface EvaluatedSchedule {
  content: ContentRef | null;
  rule: ScheduleRule | null;
}

/** Does this rule apply at `at` in the screen's time zone? */
export function ruleActive(rule: ScheduleRule, at: Date, timeZone: string): boolean {
  if (rule.enabled === false) return false;
  const p = localParts(at, timeZone);
  if (rule.startDate && p.date < rule.startDate) return false;
  if (rule.endDate && p.date > rule.endDate) return false;

  const start = parseHm(rule.startTime ?? null);
  const end = parseHm(rule.endTime ?? null);
  const days = rule.days && rule.days.length > 0 ? rule.days : null;

  if (start !== null && end !== null && start > end) {
    // Overnight range: the part after midnight belongs to the previous day's selection.
    if (p.minutes >= start) return !days || days.includes(p.weekday);
    if (p.minutes < end) return !days || days.includes((p.weekday + 6) % 7);
    return false;
  }
  if (days && !days.includes(p.weekday)) return false;
  if (start === null && end === null) return true;
  return inTimeRange(p.minutes, start ?? 0, end ?? 24 * 60);
}

/**
 * Picks the content to play right now: the active rule with the highest
 * priority; on ties the rule defined last wins; else the fallback.
 */
export function evaluateSchedule(
  rules: ScheduleRule[],
  at: Date,
  timeZone: string,
  fallback: ContentRef | null = null,
): EvaluatedSchedule {
  let best: ScheduleRule | null = null;
  for (const rule of rules) {
    if (!ruleActive(rule, at, timeZone)) continue;
    if (!best || rule.priority >= best.priority) best = rule;
  }
  return best ? { content: best.content, rule: best } : { content: fallback, rule: null };
}

/** Rules that are simultaneously active at some moment and share the top priority. */
export function findConflicts(
  rules: ScheduleRule[],
  timeZone: string,
  from: Date,
  days = 7,
): Array<[string, string]> {
  const conflicts = new Set<string>();
  const stepMs = 15 * 60_000;
  const end = from.getTime() + days * 86_400_000;
  for (let t = from.getTime(); t < end; t += stepMs) {
    const at = new Date(t);
    const active = rules.filter((r) => ruleActive(r, at, timeZone));
    if (active.length < 2) continue;
    const top = Math.max(...active.map((r) => r.priority));
    const tied = active.filter((r) => r.priority === top);
    for (let i = 0; i < tied.length; i++)
      for (let j = i + 1; j < tied.length; j++) {
        const a = tied[i]!.id;
        const b = tied[j]!.id;
        conflicts.add(a < b ? `${a}|${b}` : `${b}|${a}`);
      }
  }
  return [...conflicts].map((k) => k.split('|') as [string, string]);
}

// ── Playlist item filtering (validity period + dayparts) ──

export interface Daypart {
  days?: number[];
  startTime: string;
  endTime: string;
}

export interface PlaylistItemTiming {
  validFrom?: string | null; // ISO date-time or YYYY-MM-DD
  validUntil?: string | null;
  dayparts?: Daypart[];
}

export function itemPlayable(item: PlaylistItemTiming, at: Date, timeZone: string): boolean {
  const p = localParts(at, timeZone);
  if (item.validFrom) {
    const from =
      item.validFrom.length === 10
        ? item.validFrom
        : localParts(new Date(item.validFrom), timeZone).date;
    if (item.validFrom.length === 10 ? p.date < from : at < new Date(item.validFrom)) return false;
  }
  if (item.validUntil) {
    if (item.validUntil.length === 10 ? p.date > item.validUntil : at > new Date(item.validUntil))
      return false;
  }
  if (item.dayparts && item.dayparts.length > 0) {
    return item.dayparts.some((d) =>
      ruleActive(
        {
          id: 'dp',
          content: { kind: 'media', id: '' },
          priority: 0,
          days: d.days,
          startTime: d.startTime,
          endTime: d.endTime,
        },
        at,
        timeZone,
      ),
    );
  }
  return true;
}

/** Is the screen supposed to be powered on at `at` (power schedule)? */
export function screenPoweredOn(
  powerOn: string | null | undefined,
  powerOff: string | null | undefined,
  at: Date,
  timeZone: string,
): boolean {
  const on = parseHm(powerOn ?? null);
  const off = parseHm(powerOff ?? null);
  if (on === null || off === null) return true;
  return inTimeRange(localParts(at, timeZone).minutes, on, off);
}
